import { AddressResult } from '../types';

export interface RecentSearch {
    id: string;
    address: AddressResult;
    timestamp: number;
    searchCount: number;
}

const RECENT_SEARCHES_KEY = 'mytaxifriend_recent_searches';
const MAX_RECENT_SEARCHES = 10;

// Get recent searches from session storage
export const getRecentSearches = (): RecentSearch[] => {
    try {
        const stored = sessionStorage.getItem(RECENT_SEARCHES_KEY);
        if (stored) {
            const searches = JSON.parse(stored);
            // Sort by most recent first
            return searches.sort((a: RecentSearch, b: RecentSearch) => b.timestamp - a.timestamp);
        }
    } catch (error) {
        console.error('Error loading recent searches:', error);
    }
    return [];
};

// Save recent searches to session storage
export const saveRecentSearches = (searches: RecentSearch[]): void => {
    try {
        sessionStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(searches));
    } catch (error) {
        console.error('Error saving recent searches:', error);
    }
};

// Add a new search to recent searches
export const addRecentSearch = (address: AddressResult): void => {
    const recentSearches = getRecentSearches();
    const now = Date.now();
    
    // Check if this address already exists in recent searches
    const existingIndex = recentSearches.findIndex(
        search => search.address.name === address.name && 
                  search.address.district === address.district
    );
    
    if (existingIndex >= 0) {
        // Update existing search
        recentSearches[existingIndex].timestamp = now;
        recentSearches[existingIndex].searchCount += 1;
    } else {
        // Add new search
        const newSearch: RecentSearch = {
            id: `search_${now}_${Math.random().toString(36).substr(2, 9)}`,
            address,
            timestamp: now,
            searchCount: 1
        };
        recentSearches.unshift(newSearch);
    }
    
    // Keep only the most recent searches
    const limitedSearches = recentSearches.slice(0, MAX_RECENT_SEARCHES);
    saveRecentSearches(limitedSearches);
};

// Remove a search from recent searches
export const removeRecentSearch = (searchId: string): void => {
    const recentSearches = getRecentSearches();
    const filteredSearches = recentSearches.filter(search => search.id !== searchId);
    saveRecentSearches(filteredSearches);
};

// Clear all recent searches
export const clearRecentSearches = (): void => {
    try {
        sessionStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (error) {
        console.error('Error clearing recent searches:', error);
    }
};

// Get search frequency for an address
export const getSearchFrequency = (address: AddressResult): number => {
    const recentSearches = getRecentSearches();
    const existingSearch = recentSearches.find(
        search => search.address.name === address.name && 
                  search.address.district === address.district
    );
    return existingSearch ? existingSearch.searchCount : 0;
};

// Format timestamp for display
export const formatSearchTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) {
        return 'Just now';
    } else if (minutes < 60) {
        return `${minutes}m ago`;
    } else if (hours < 24) {
        return `${hours}h ago`;
    } else if (days < 7) {
        return `${days}d ago`;
    } else {
        return new Date(timestamp).toLocaleDateString();
    }
};

// Check if session storage is available
export const isSessionStorageAvailable = (): boolean => {
    try {
        const test = '__test__';
        sessionStorage.setItem(test, test);
        sessionStorage.removeItem(test);
        return true;
    } catch (error) {
        return false;
    }
};
